import { DestroyRef, Injectable, Signal, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription, of, switchMap } from 'rxjs';
import { HeldOrder } from '../models/sale.model';
import { NewDoc, TenantDb } from '../data/tenant-collection';
import { BusinessContextService } from './multi-tenant/business-context.service';

/**
 * Soft cap on paused orders per Location (ADR-0007). Deliberately a soft bound: there is
 * no auto-expiry, and two terminals can momentarily push the shared pool past it.
 */
export const MAX_HELD_ORDERS = 20;

/**
 * Domain fields a caller supplies to pause a cart. `locationId` and the audit fields are
 * stamped by the service, so they are omitted here.
 */
export type NewHeldOrder = Omit<NewDoc<HeldOrder>, 'locationId'>;

/** Path of the location-scoped held-orders collection under `businesses/{businessId}`. */
const HELD_ORDERS_PATH = 'held_orders';

/**
 * Owns the location-scoped `held_orders` Business collection (ADR-0007). Every terminal at a
 * Location shares one pool of paused carts, read in real time so a cart paused on one terminal
 * is visible and resumable on another. All Firestore access goes through the TenantCollection
 * seam; the live list is the auditable exception to the project's Promise-first default.
 */
@Injectable({ providedIn: 'root' })
export class HeldOrderService {
  private readonly db = inject(TenantDb);
  private readonly heldOrdersCollection = this.db.collection<HeldOrder>(HELD_ORDERS_PATH);
  private readonly businessContext = inject(BusinessContextService);

  private syncSub?: Subscription;

  private readonly _heldOrders = signal<HeldOrder[]>([]);
  /** Live, location-scoped pool of paused orders (newest first). Empty until {@link startLiveSync}. */
  readonly heldOrders: Signal<HeldOrder[]> = this._heldOrders.asReadonly();
  readonly heldOrdersCount = computed(() => this._heldOrders().length);
  readonly canHoldMore = computed(() => this._heldOrders().length < MAX_HELD_ORDERS);

  /**
   * Pause a cart into the shared pool for the current Location. Returns `false` when the soft
   * cap is reached or no Location is selected. Sale attribution is intentionally NOT set here —
   * it is reassigned to whoever resumes and closes the sale (ADR-0007); `heldByName` on the
   * payload is only a reference to who paused it.
   */
  async hold(order: NewHeldOrder): Promise<boolean> {
    if (!this.canHoldMore()) {
      return false;
    }
    const locationId = await this.businessContext.getCurrentStoreId();
    if (!locationId) {
      return false;
    }
    await this.heldOrdersCollection.add({ ...order, locationId } as NewDoc<HeldOrder>);
    return true;
  }

  /**
   * Atomically claim a paused order: read it and, if present, delete it inside one Firestore
   * transaction (ADR-0007). Returns the claimed order, or `null` when it no longer exists — under
   * concurrency, that means another terminal already claimed it. Only one terminal can win a
   * concurrent claim: the transaction that reads and deletes the doc commits; any other that
   * raced on the same doc re-runs, finds it gone, and returns `null`. Shared by {@link resume}
   * and {@link delete} so the two can never race into a double-take.
   */
  private claim(orderId: string): Promise<HeldOrder | null> {
    return this.db.runTransaction(async txn => {
      const order = await txn.get<HeldOrder>(HELD_ORDERS_PATH, orderId);
      if (!order) {
        return null;
      }
      txn.delete(HELD_ORDERS_PATH, orderId);
      return order;
    });
  }

  /**
   * Resume a paused order: claim it and return it so the caller can restore the cart, or `null`
   * if another terminal already took it.
   */
  resume(orderId: string): Promise<HeldOrder | null> {
    return this.claim(orderId);
  }

  /**
   * Remove a paused order without resuming it. Returns `false` when it no longer exists (already
   * claimed or deleted by another terminal).
   */
  delete(orderId: string): Promise<boolean> {
    return this.claim(orderId).then(order => order !== null);
  }

  /**
   * Start mirroring the current Location's `held_orders` into the live pool, re-subscribing
   * whenever the active Location changes, until `destroyRef` fires.
   *
   * The lifecycle is owned by the caller (the POS), not this root service: the Firestore listener
   * lives only while the POS is mounted — not for the whole app session — and tears down with the
   * POS via its `DestroyRef`. A single equality filter keeps this off any composite index; the
   * ≤20-doc pool is ordered newest-first client-side.
   *
   * Idempotent and remount-safe: any previous sync is dropped first, and the stale pool is cleared
   * so a re-mount (or a Location change) never briefly shows the previous Location's orders.
   */
  startLiveSync(destroyRef: DestroyRef): void {
    this.syncSub?.unsubscribe();
    this._heldOrders.set([]);
    this.syncSub = this.businessContext.currentLocationId$
      .pipe(
        switchMap(locationId =>
          locationId
            ? this.heldOrdersCollection.observe({ where: [['locationId', '==', locationId]] })
            : of<HeldOrder[]>([])
        ),
        takeUntilDestroyed(destroyRef)
      )
      .subscribe({
        next: orders =>
          this._heldOrders.set([...orders].sort((a, b) => b.heldAt.getTime() - a.heldAt.getTime())),
        error: err => console.error('[HeldOrderService] held_orders observe error', err),
      });
  }
}
